<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import API from 'dashboard/api/whatsappTemplateDrafts';
import Button from 'dashboard/components-next/button/Button.vue';
import TemplatePreview from 'dashboard/components-next/template-preview/TemplatePreview.vue';
import {
  draftFingerprint,
  draftPreview,
  emptyDraft,
  exampleValue,
  setButtonUrlExample,
  setExample,
  updateButtonUrl,
  validateDraft,
  variableKeys,
} from './draftUtils';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
  inboxes: { type: Array, required: true },
});
const { t } = useI18n();
const store = useStore();
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);
const label = key => t(`WHATSAPP_TEMPLATE_MGMT.DRAFT.${key}`);
const clone = value => JSON.parse(JSON.stringify(value));
const inboxId = ref('');
const records = ref([]);
const cursor = ref(null);
const record = ref(null);
const draft = ref(emptyDraft());
const headerMediaUploadId = ref(null);
const headerMediaExample = ref(null);
const headerMediaPreviewUrl = ref('');
const error = ref('');
const fields = ref([]);
const uncertainOperation = ref(null);
const { run, isPending } = useAbortableRequest();
const dirty = computed(
  () =>
    draftFingerprint(draft.value) !==
      draftFingerprint(record.value?.definition || emptyDraft()) ||
    headerMediaUploadId.value !== (record.value?.header_media_upload_id || null)
);
const locked = computed(() => isPending.value || !!uncertainOperation.value);
const cloudInboxes = computed(() =>
  props.inboxes.filter(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' &&
      inbox.provider === 'whatsapp_cloud'
  )
);
const textComponents = computed(() =>
  draft.value.components.filter(
    component =>
      ['HEADER', 'BODY', 'FOOTER'].includes(component.type) &&
      typeof component.text === 'string'
  )
);
const buttons = computed(
  () =>
    draft.value.components.find(component => component.type === 'BUTTONS')
      ?.buttons || []
);
const headerFormat = computed(
  () =>
    draft.value.components.find(component => component.type === 'HEADER')
      ?.format || 'NONE'
);
const headerMediaAccept = computed(
  () =>
    ({
      IMAGE: 'image/jpeg,image/png',
      VIDEO: 'video/mp4',
      DOCUMENT: 'application/pdf',
    })[headerFormat.value] || ''
);
const preview = computed(() => {
  const value = draftPreview(draft.value);
  const header = value.components.find(
    component => component.type === 'HEADER'
  );
  if (header && headerMediaPreviewUrl.value)
    header.example = { header_handle: [headerMediaPreviewUrl.value] };
  return value;
});
const submission = computed(() => record.value?.submission || {});
const state = computed(() => submission.value.state || 'draft');
const senderCatalogState = ref('unselected');
let senderCatalogRequest = 0;
const templateEligibility = computed(() => {
  if (state.value !== 'approved') return 'NOT_APPROVED';
  if (
    dirty.value ||
    !submission.value.snapshot ||
    draftFingerprint(submission.value.snapshot) !==
      draftFingerprint(record.value?.definition) ||
    (submission.value.header_media_upload_id || null) !==
      (record.value?.header_media_upload_id || null)
  )
    return 'REVISION_NOT_APPROVED';
  if (senderCatalogState.value === 'loading') return 'CHECKING';
  if (senderCatalogState.value !== 'loaded') return 'UNKNOWN';
  if (!submission.value.external_id) return 'NOT_SYNCED';

  const templates = getFilteredWhatsAppTemplates.value(Number(inboxId.value));
  return templates.some(
    template => String(template.id) === String(submission.value.external_id)
  )
    ? 'SENDABLE'
    : 'NOT_SYNCED';
});
const submissionLocked = computed(() =>
  [
    'queued',
    'running',
    'pending',
    'unknown',
    'paused',
    'disabled',
    'deleted',
  ].includes(state.value)
);

function clearHeaderMediaPreview() {
  if (headerMediaPreviewUrl.value) {
    URL.revokeObjectURL(headerMediaPreviewUrl.value);
  }
  headerMediaPreviewUrl.value = '';
}

function setHeaderMediaPreview(file) {
  clearHeaderMediaPreview();
  if (typeof URL.createObjectURL === 'function')
    headerMediaPreviewUrl.value = URL.createObjectURL(file);
}

onUnmounted(clearHeaderMediaPreview);

async function loadList(more = false) {
  if (locked.value || !inboxId.value) return;
  error.value = '';
  if (!more) {
    records.value = [];
    record.value = null;
    draft.value = emptyDraft();
    headerMediaUploadId.value = null;
    headerMediaExample.value = null;
    clearHeaderMediaPreview();
    cursor.value = null;
  }
  await run(async signal => {
    try {
      const { data } = await API.list(
        props.accountId,
        inboxId.value,
        more ? cursor.value : null,
        signal
      );
      if (signal.aborted) return;
      records.value = more ? [...records.value, ...data.payload] : data.payload;
      cursor.value = data.next_cursor;
    } catch {
      if (!signal.aborted) error.value = label('LOAD_ERROR');
    }
  });
}

async function loadInbox() {
  const selectedInboxId = inboxId.value;
  senderCatalogRequest += 1;
  const request = senderCatalogRequest;
  senderCatalogState.value = selectedInboxId ? 'loading' : 'unselected';
  loadList();
  if (!selectedInboxId) return;

  try {
    const loaded = await store.dispatch(
      'inboxes/fetchMessageTemplates',
      Number(selectedInboxId)
    );
    if (request === senderCatalogRequest && inboxId.value === selectedInboxId)
      senderCatalogState.value = loaded ? 'loaded' : 'error';
  } catch {
    if (request === senderCatalogRequest && inboxId.value === selectedInboxId)
      senderCatalogState.value = 'error';
  }
}

async function selectRecord(id) {
  if (locked.value || dirty.value) return;
  error.value = '';
  await run(async signal => {
    try {
      const { data } = await API.show(
        props.accountId,
        inboxId.value,
        id,
        signal
      );
      if (signal.aborted) return;
      if (dirty.value) {
        error.value = label('READ_EDIT_PRESERVED');
        return;
      }
      record.value = data.payload;
      draft.value = clone(data.payload.definition);
      headerMediaUploadId.value = data.payload.header_media_upload_id || null;
      headerMediaExample.value = data.payload.header_media_example || null;
      clearHeaderMediaPreview();
      fields.value = [];
    } catch {
      if (!signal.aborted) error.value = label('LOAD_ERROR');
    }
  });
}

function discard() {
  if (locked.value) return;
  draft.value = clone(record.value?.definition || emptyDraft());
  headerMediaUploadId.value = record.value?.header_media_upload_id || null;
  headerMediaExample.value = record.value?.header_media_example || null;
  clearHeaderMediaPreview();
  fields.value = [];
}

function newDraft() {
  if (locked.value || dirty.value) return;
  record.value = null;
  draft.value = emptyDraft();
  headerMediaUploadId.value = null;
  headerMediaExample.value = null;
  clearHeaderMediaPreview();
  fields.value = [];
  error.value = '';
}

function changeHeader(format) {
  const previous = draft.value.components.find(
    component => component.type === 'HEADER'
  );
  if (previous?.text) {
    error.value = label('REMOVE_HEADER_FIRST');
    return;
  }
  draft.value.components = draft.value.components.filter(
    component => component.type !== 'HEADER'
  );
  if (format !== previous?.format) {
    headerMediaUploadId.value = null;
    headerMediaExample.value = null;
    clearHeaderMediaPreview();
  }
  if (format !== 'NONE')
    draft.value.components.unshift({
      type: 'HEADER',
      format,
      ...(format === 'TEXT' ? { text: '' } : {}),
    });
}

async function uploadHeaderMedia(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file || !inboxId.value || !headerMediaAccept.value) return;

  const selectedInboxId = inboxId.value;
  const selectedFormat = headerFormat.value;
  error.value = '';
  await run(async signal => {
    try {
      const { data } = await API.upload(
        props.accountId,
        selectedInboxId,
        file,
        selectedFormat,
        crypto.randomUUID(),
        signal
      );
      if (
        signal.aborted ||
        inboxId.value !== selectedInboxId ||
        headerFormat.value !== selectedFormat
      )
        return;
      headerMediaUploadId.value = data.payload.id;
      headerMediaExample.value = data.payload;
      setHeaderMediaPreview(file);
    } catch (exception) {
      if (!signal.aborted) {
        const code = exception.response?.data?.error?.code;
        error.value =
          code === 'upload_too_large'
            ? label('UPLOAD_TOO_LARGE')
            : label('UPLOAD_ERROR');
      }
    }
  });
}

function addFooter() {
  if (!draft.value.components.some(component => component.type === 'FOOTER'))
    draft.value.components.push({ type: 'FOOTER', text: '' });
}

function addButton() {
  let component = draft.value.components.find(item => item.type === 'BUTTONS');
  if (!component) {
    component = { type: 'BUTTONS', buttons: [] };
    draft.value.components.push(component);
    component = draft.value.components.find(item => item.type === 'BUTTONS');
  }
  component.buttons.push({ type: 'QUICK_REPLY', text: '' });
}

function removeButton(index) {
  buttons.value.splice(index, 1);
  if (!buttons.value.length)
    draft.value.components = draft.value.components.filter(
      component => component.type !== 'BUTTONS'
    );
}

function changeButtonType(button, type) {
  delete button.url;
  delete button.phone_number;
  delete button.example;
  button.type = type;
  if (type === 'URL') button.url = '';
  if (type === 'PHONE_NUMBER') button.phone_number = '';
}

function submissionErrorLabel(code) {
  if (code === 'stale_revision') return 'CONFLICT';
  if (code === 'provider_validation_error') return 'PROVIDER_VALIDATION_ERROR';
  if (code === 'waba_shared_across_accounts') return 'SHARED_WABA';
  if (code === 'provider_capabilities_unverified')
    return 'CAPABILITIES_UNVERIFIED';
  if (code === 'operation_in_progress') return 'SUBMIT_IN_PROGRESS';
  if (code === 'upload_expired') return 'UPLOAD_EXPIRED';
  return 'SUBMIT_UNKNOWN';
}

function validationLabel(field) {
  if (field !== 'header.media_example_unavailable') return field;
  return label(
    headerMediaExample.value?.expired
      ? 'UPLOAD_EXPIRED'
      : 'HEADER_MEDIA_REQUIRED'
  );
}

async function executeOperation() {
  if (isPending.value || !uncertainOperation.value) return;
  const operation = uncertainOperation.value;
  error.value = '';
  await run(async signal => {
    try {
      const { data } = await API.mutate(
        props.accountId,
        inboxId.value,
        operation,
        signal
      );
      if (signal.aborted) return;
      // Never replace the editor: typing during save or submission is retained.
      record.value = data.payload;
      const index = records.value.findIndex(
        item => item.id === data.payload.id
      );
      if (index < 0) records.value.push(data.payload);
      else records.value[index] = data.payload;
      uncertainOperation.value = null;
    } catch (exception) {
      if (signal.aborted) return;
      const status = exception.response?.status;
      const code = exception.response?.data?.error?.code;
      if (operation.action === 'submit') {
        uncertainOperation.value = null;
        error.value = label(submissionErrorLabel(code));
      } else if (
        status >= 400 &&
        status < 500 &&
        status !== 408 &&
        status !== 429
      ) {
        uncertainOperation.value = null;
        error.value =
          code === 'stale_revision' ? label('CONFLICT') : label('SAVE_ERROR');
        fields.value = exception.response?.data?.error?.fields || [];
      } else error.value = label('UNKNOWN');
    }
  });
}
async function mutate(action) {
  if (locked.value || !inboxId.value) return;
  fields.value = validateDraft(
    draft.value,
    action === 'submit',
    Boolean(headerMediaUploadId.value && !headerMediaExample.value?.expired)
  );
  if (fields.value.length) return;
  const body =
    action === 'create' || action === 'update'
      ? {
          template: clone(draft.value),
          header_media_upload_id: headerMediaUploadId.value,
          ...(record.value ? { lock_version: record.value.lock_version } : {}),
        }
      : {
          lock_version: record.value.lock_version,
        };
  uncertainOperation.value = {
    action,
    id: record.value?.id,
    body,
    key: crypto.randomUUID(),
  };
  await executeOperation();
}
</script>

<template>
  <section
    class="p-4 my-4 border rounded-xl border-n-weak"
    :aria-label="label('TITLE')"
  >
    <h2 class="text-lg font-medium">{{ label('TITLE') }}</h2>
    <p class="text-sm text-n-slate-11">{{ label('NOTICE') }}</p>
    <label class="block mt-3">
      {{ label('INBOX') }}
      <select
        v-model="inboxId"
        data-testid="draft-inbox"
        :disabled="locked || dirty"
        @change="loadInbox"
      >
        <option value="">{{ label('SELECT_INBOX') }}</option>
        <option
          v-for="inbox in cloudInboxes"
          :key="inbox.id"
          :value="String(inbox.id)"
        >
          {{ inbox.name }}
        </option>
      </select>
    </label>
    <p v-if="!cloudInboxes.length">{{ label('NO_CLOUD') }}</p>
    <p v-if="error" role="alert" class="text-n-ruby-11">{{ error }}</p>
    <p v-if="fields.length" role="alert" class="text-n-ruby-11">
      {{ label('INVALID') }}: {{ fields.map(validationLabel).join(', ') }}
    </p>
    <Button
      v-if="uncertainOperation"
      :label="label('RETRY')"
      :disabled="isPending"
      @click="executeOperation"
    />
    <div v-if="inboxId" class="flex flex-col gap-4 mt-3">
      <div class="flex flex-wrap gap-2">
        <Button
          :label="label('NEW')"
          :disabled="locked || dirty"
          @click="newDraft"
        />
        <Button
          :label="label('RELOAD')"
          :disabled="locked || dirty"
          @click="loadInbox"
        />
        <Button
          v-for="item in records"
          :key="item.id"
          :label="`${item.name} · ${item.language}`"
          :disabled="locked || dirty"
          @click="selectRecord(item.id)"
        />
        <Button
          v-if="cursor"
          :label="label('MORE')"
          :disabled="locked"
          @click="loadList(true)"
        />
      </div>
      <div class="grid gap-6 lg:grid-cols-2">
        <div class="flex flex-col gap-3">
          <label
            >{{ label('NAME')
            }}<input
              v-model="draft.name"
              data-testid="draft-name"
              :disabled="!!submission.operation_id"
          /></label>
          <label
            >{{ label('LANGUAGE')
            }}<input
              v-model="draft.language"
              data-testid="draft-language"
              :disabled="!!submission.operation_id"
          /></label>
          <label
            >{{ label('CATEGORY')
            }}<select v-model="draft.category">
              <option value="UTILITY">{{ label('UTILITY') }}</option>
              <option value="MARKETING">{{ label('MARKETING') }}</option>
            </select></label
          >
          <label
            >{{ label('FORMAT')
            }}<select v-model="draft.parameter_format">
              <option value="POSITIONAL">{{ label('POSITIONAL') }}</option>
              <option value="NAMED">{{ label('NAMED') }}</option>
            </select></label
          >
          <p class="text-sm text-n-slate-11">{{ label('VARIABLE_HELP') }}</p>
          <label
            >{{ label('HEADER')
            }}<select
              data-testid="draft-header-format"
              :value="headerFormat"
              :disabled="locked || submissionLocked"
              @change="changeHeader($event.target.value)"
            >
              <option
                v-for="format in ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT']"
                :key="format"
                :value="format"
              >
                {{ label(format) }}
              </option>
            </select></label
          >
          <template v-if="!['NONE', 'TEXT'].includes(headerFormat)">
            <label class="block">
              {{ label('HEADER_MEDIA') }}
              <input
                data-testid="header-media-upload"
                type="file"
                :accept="headerMediaAccept"
                :disabled="locked || submissionLocked"
                @change="uploadHeaderMedia"
              />
            </label>
            <p v-if="headerMediaExample?.expired" class="text-n-amber-11">
              {{ label('UPLOAD_EXPIRED') }}
            </p>
            <p v-else-if="headerMediaExample" class="text-sm text-n-slate-11">
              {{ label('HEADER_MEDIA_SELECTED') }}:
              {{ headerMediaExample.filename }}
            </p>
            <p v-else class="text-sm text-n-slate-11">
              {{ label('HEADER_MEDIA_REQUIRED') }}
            </p>
          </template>
          <div v-for="component in textComponents" :key="component.type">
            <label
              >{{ label(component.type)
              }}<textarea
                v-model="component.text"
                :data-testid="`draft-${component.type.toLowerCase()}`"
              />
            </label>
            <label
              v-for="key in variableKeys(component.text)"
              :key="key"
              class="block"
            >
              {{ label('EXAMPLE') }} {{ component.type }} / {{ key }}
              <input
                :data-testid="`example-${component.type}-${key}`"
                :value="exampleValue(component, key, draft.parameter_format)"
                @input="
                  setExample(
                    component,
                    key,
                    $event.target.value,
                    draft.parameter_format
                  )
                "
              />
            </label>
          </div>
          <Button :label="label('ADD_FOOTER')" @click="addFooter" />
          <div
            v-for="(button, index) in buttons"
            :key="index"
            class="p-2 border rounded border-n-weak"
          >
            <label
              >{{ label('BUTTON_TYPE')
              }}<select
                data-testid="button-type"
                :value="button.type"
                @change="changeButtonType(button, $event.target.value)"
              >
                <option value="QUICK_REPLY">{{ label('QUICK_REPLY') }}</option>
                <option value="URL">{{ label('URL') }}</option>
                <option value="PHONE_NUMBER">{{ label('PHONE') }}</option>
              </select></label
            >
            <label>
              {{ label('BUTTON_TEXT') }}
              <input v-model="button.text" data-testid="button-text" />
            </label>
            <label v-if="button.type === 'URL'">
              {{ label('URL') }}
              <input
                data-testid="button-url"
                :value="button.url"
                @input="updateButtonUrl(button, $event.target.value)"
              />
            </label>
            <label v-if="button.type === 'URL' && button.url?.includes('{{')">
              {{ label('URL_EXAMPLE') }}
              <input
                data-testid="button-url-example"
                :value="button.example?.[0] || ''"
                @input="setButtonUrlExample(button, $event.target.value)"
              />
            </label>
            <label v-if="button.type === 'PHONE_NUMBER'"
              >{{ label('PHONE') }}<input v-model="button.phone_number"
            /></label>
            <Button
              :label="label('REMOVE_BUTTON')"
              @click="removeButton(index)"
            />
          </div>
          <Button
            :label="label('ADD_BUTTON')"
            :disabled="buttons.length >= 3"
            @click="addButton"
          />
          <p v-if="dirty" role="status">{{ label('UNSAVED') }}</p>
          <div class="flex flex-wrap gap-2">
            <Button
              data-testid="save-draft"
              :label="label('SAVE')"
              :disabled="locked || !dirty"
              @click="mutate(record ? 'update' : 'create')"
            />
            <Button
              :label="label('DISCARD')"
              :disabled="locked || !dirty"
              @click="discard"
            />
            <Button
              data-testid="submit-draft"
              :label="label('SUBMIT')"
              :disabled="locked || dirty || !record || submissionLocked"
              @click="mutate('submit')"
            />
          </div>
        </div>
        <div class="flex flex-col gap-3">
          <h3>{{ label('PREVIEW') }}</h3>
          <TemplatePreview :template="preview" platform="whatsapp" />
          <div
            v-for="(button, index) in buttons"
            :key="index"
            class="p-2 border rounded border-n-weak"
          >
            {{ button.text }}
            <span class="block text-xs">{{
              button.url || button.phone_number
            }}</span>
          </div>
          <p role="status">
            {{ label('SUBMISSION') }}:
            {{ label(`STATE_${state.toUpperCase()}`) }}
          </p>
          <p v-if="submission.revision !== undefined">
            {{ label('REVISION') }} {{ submission.revision }}
          </p>
          <p v-if="submission.external_id">
            {{ label('EXTERNAL_ID') }} {{ submission.external_id }}
          </p>
          <p v-if="submission.provider_status">
            {{ label('PROVIDER_STATUS') }} {{ submission.provider_status }}
          </p>
          <p data-testid="template-eligibility" role="status">
            {{ label(`ELIGIBILITY_${templateEligibility}`) }}
          </p>
          <p v-if="submission.error" role="alert">
            {{
              label(
                submission.error === 'upload_expired'
                  ? 'UPLOAD_EXPIRED'
                  : 'SUBMIT_ERROR'
              )
            }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
